import * as React from 'react';
import { getNewValue } from '../../constants';
import InputDropdown from '../../widgets/InputDropdown';
import { OptionType } from '../../types';
import { GroupedOptionsType, ValueType } from 'react-select';
import { Input } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { memoizedHardwareSelectors } from '../../selectors';
import { apiRequest } from '../../APIClient';
import { OnboardingModal } from './OnboardingModal';
import {
  memoizedOnboardingSelectors,
  onboardingSelector,
} from '../../reducers/onboarding';

type Props = {
  visible: boolean;
  onCancel: () => void;
};
export const AllotmentUpdateModal: React.FunctionComponent<Props> = (props) => {
  const { visible, onCancel } = props;

  const configuredSeriesMap = useSelector(
    memoizedHardwareSelectors.getConfiguredGenericSeries,
  );
  const purchasedSeriesMap = useSelector(
    memoizedHardwareSelectors.getPurchasedGenericSeries,
  );
  const allAvailableSeries = useSelector(
    onboardingSelector.getAvailableGenericSensors,
  );
  const genericSeriesEnabled = useSelector(
    onboardingSelector.showGenericSeries,
  );

  const configuredHardwareMap = useSelector(
    memoizedHardwareSelectors.getConfiguredQuantityMap,
  );
  const hardwareQuantity = useSelector(onboardingSelector.getHardwareQuantity);
  const availableHardware = useSelector(
    memoizedOnboardingSelectors.getAvailableHardware,
  );
  const dispatch = useDispatch();

  const [model, setModel] = React.useState<OptionType | null>(null);
  const onModelChange = React.useCallback(
    (v: ValueType<OptionType>) => {
      const newValue = getNewValue(v, false);
      setModel(newValue);
    },
    [setModel],
  );

  const [quantity, setQuantity] = React.useState<number>(0);
  const onQuantityChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) =>
      setQuantity(e.currentTarget.value ? parseInt(e.currentTarget.value) : 0),
    [setQuantity],
  );

  const [quantityConfigured, setQuantityConfigured] = React.useState<
    string | number
  >('N/A');

  React.useEffect(() => {
    if (model) {
      const hardwareType = model.value;
      if (hardwareType.includes('series')) {
        setQuantityConfigured(configuredSeriesMap[hardwareType] ?? 0);
        setQuantity(purchasedSeriesMap[hardwareType] ?? 0);
      } else {
        setQuantityConfigured(configuredHardwareMap[hardwareType] ?? 0);
        setQuantity(hardwareQuantity?.[hardwareType] ?? 0);
      }
    }
  }, [
    configuredHardwareMap,
    setQuantityConfigured,
    setQuantity,
    model,
    hardwareQuantity,
    configuredSeriesMap,
    purchasedSeriesMap,
  ]);

  const onOk = React.useCallback(() => {
    if (model) {
      dispatch(apiRequest.updateAllotment(model.value, quantity));
    }
    onCancel();
  }, [model, quantity, dispatch, onCancel]);

  const allSensorModels = React.useMemo(() => {
    const added = new Set<string>();
    const result: OptionType[] = [];

    // Use the first sensor model retrieved by the allotment key
    availableHardware.sensors.forEach((sensor) => {
      if (!added.has(sensor.allotment_key)) {
        added.add(sensor.allotment_key);
        result.push({ label: sensor.label, value: sensor.hardware_key });
      }
    });
    return result;
  }, [availableHardware.sensors]);

  const allScannerModels = React.useMemo(() => {
    const added = new Set<string>();
    const result: OptionType[] = [];
    availableHardware.scanners.forEach((scanner) => {
      if (!added.has(scanner.allotment_key)) {
        added.add(scanner.allotment_key);
        result.push({ label: scanner.label, value: scanner.hardware_key });
      }
    });
    return result;
  }, [availableHardware.scanners]);

  const groupedOptions: GroupedOptionsType<OptionType> = React.useMemo(() => {
    const options = [
      {
        label: 'Scanner',
        options: allScannerModels,
      },
    ];
    if (genericSeriesEnabled) {
      options.unshift({
        label: 'Series',
        options: allAvailableSeries,
      });
    } else {
      options.unshift({
        label: 'Sensor',
        options: allSensorModels,
      });
    }
    return options;
  }, [
    allScannerModels,
    allSensorModels,
    allAvailableSeries,
    genericSeriesEnabled,
  ]);

  return (
    <OnboardingModal
      title="Allotment Update"
      visible={visible}
      onCancel={onCancel}
      onOk={onOk}
      okDisabled={!model}
      okTitle="Update"
      showConfirm
    >
      <div style={{ minHeight: 400 }}>
        <h5 className="mb-4">Hardware Model</h5>
        <InputDropdown
          id="selectModel"
          value={model}
          onChange={onModelChange}
          options={groupedOptions}
          placeholder="Select a hardware"
          classNamePrefix="selectModel"
        />

        <h5 className="my-4">Allotted Quantity</h5>
        <Input
          data-cy="quantityInput"
          value={quantity}
          type="number"
          onChange={onQuantityChange}
        />

        <h5 className="my-4">Configured Quantity</h5>
        <span>{quantityConfigured}</span>
      </div>
    </OnboardingModal>
  );
};
